import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useRoute, useSearch } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, Eraser, Flag, History, Loader2, Lock, PenLine, Sparkles, Trash2, Undo2, Wand2 } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { useLanguage } from "@/components/language-provider";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Mascot } from "@/components/assistant/mascot";
import { ToastAction } from "@/components/ui/toast";
import { FreeTextView, TemplateView } from "@/components/library/template-view";
import { AiAdjustDialog, aiButtonState, useAiStatus } from "@/components/library/ai-adjust-dialog";
import {
  AI_CHANGED, PENDING_UPLOAD_KEY, categoryName, deleteMine, fetchCategories, fetchModel, fetchMyTemplate, formatCep, formatCpfCnpj, formatPhone,
  renderMine, renderModel, renderValue, undoMine, type AdjustResult, type ContractField,
} from "@/lib/library-client";

const TEXTS = {
  pt: {
    back: "Biblioteca",
    name: "Nome do documento",
    fields: "Campos",
    progress: (a: number, b: number) => `${a} de ${b} preenchidos`,
    allDone: "Tudo preenchido!",
    contractGroup: "Dados do contrato",
    freeText: "Texto livre",
    freeTextHint: "Edite qualquer parte do texto. Os campos destacados viram texto normal.",
    freeTextOffTitle: "Voltar aos campos?",
    freeTextOffDesc: "As alterações feitas no texto livre serão descartadas; os valores dos campos ficam guardados.",
    keep: "Continuar editando",
    discard: "Descartar e voltar",
    continueBtn: "Continuar para assinatura",
    generating: "Gerando o PDF…",
    blankTitle: "Campos em branco",
    blankDesc: (n: number) => `${n} ${n === 1 ? "campo ficará" : "campos ficarão"} em branco (linha para preencher à mão). Continuar mesmo assim?`,
    fill: "Voltar e preencher",
    goOn: "Continuar",
    clear: "Limpar campos",
    noFields: "Este modelo não tem campos detectados: ligue o texto livre para editar o que precisar.",
    tip: "Clique em um campo do texto ou use a lista de campos. Valores em dinheiro ganham o valor por extenso automaticamente.",
    mascotHello: (n: number) => n === 0 ? "Esse modelo é texto corrido. Liga o texto livre e edita à vontade!" : `Preenche os ${n} campos destacados e eu monto o PDF pra você assinar.`,
    mascotDone: "Pronto! Agora é só continuar para escolher quem assina.",
    mascotProgress: (left: number) => left === 1 ? "Falta só 1 campo!" : `Faltam ${left} campos.`,
    limit: "Limite mensal de documentos atingido.",
    plans: "Ver planos",
    failed: "Não foi possível gerar o PDF agora. Tente de novo em instantes.",
    note: "Modelo brasileiro de referência. Revise o texto antes de enviar para assinatura.",
    preview: "No documento:",
    optional: "opcional",
    other: "Outro…",
    ai: "Ajustar com IA",
    aiAgain: "Ajustar de novo",
    aiPaid: "planos pagos",
    aiFreeTextOff: "Desligue o texto livre para ajustar com IA.",
    mine: "Meu modelo",
    english: "Inglês",
    mineExpiry: (date: string, days: number) => `Só você vê esta cópia. Ela fica em Meus modelos até ${date} (${days <= 1 ? "falta 1 dia" : `faltam ${days} dias`}) e depois é apagada automaticamente. Documentos que você já enviou para assinatura não são afetados.`,
    mineEnglish: "Tradução de um modelo brasileiro: o contrato continua seguindo a lei brasileira (foro no Brasil). Revise com atenção antes de enviar.",
    changedTitle: "O que a IA mudou",
    asked: "Pedido:",
    changedHint: "Os trechos alterados têm uma barra roxa na margem (ela não sai no PDF).",
    changedMost: "A IA mudou quase todo o texto, então o destaque começa desligado.",
    highlight: "Destacar mudanças",
    undo: "Desfazer último ajuste",
    undone: "Último ajuste desfeito.",
    remove: "Excluir cópia",
    removeTitle: "Excluir esta cópia?",
    removeDesc: "A cópia ajustada sai de Meus modelos agora. O modelo original continua na biblioteca, e o ajuste usado não volta para o limite do mês.",
    removed: "Cópia excluída.",
    original: "Ver o modelo original",
    history: (n: number) => n === 1 ? "1 ajuste nesta cópia" : `${n} ajustes nesta cópia`,
    expired: (days: number) => `Esta cópia não existe mais. As cópias ajustadas com IA ficam ${days} dias em Meus modelos e depois são apagadas.`,
    notFound: "Modelo não encontrado.",
    adjusted: "Pronto! Cópia ajustada salva em Meus modelos.",
  },
  en: {
    back: "Library",
    name: "Document name",
    fields: "Fields",
    progress: (a: number, b: number) => `${a} of ${b} filled`,
    allDone: "All filled in!",
    contractGroup: "Contract details",
    freeText: "Free text",
    freeTextHint: "Edit any part of the text. The highlighted fields become plain text.",
    freeTextOffTitle: "Back to the fields?",
    freeTextOffDesc: "Changes made in free text will be discarded; the field values are kept.",
    keep: "Keep editing",
    discard: "Discard and go back",
    continueBtn: "Continue to signature",
    generating: "Generating the PDF…",
    blankTitle: "Blank fields",
    blankDesc: (n: number) => `${n} field${n === 1 ? "" : "s"} will stay blank (a line to fill by hand). Continue anyway?`,
    fill: "Go back and fill",
    goOn: "Continue",
    clear: "Clear fields",
    noFields: "No fields were detected in this model: turn on free text to edit what you need.",
    tip: "Click a field in the text or use the field list. Money values get the amount in words automatically.",
    mascotHello: (n: number) => n === 0 ? "This model is plain text. Turn on free text and edit away!" : `Fill in the ${n} highlighted fields and I'll build the PDF for signing.`,
    mascotDone: "Done! Continue to choose who signs.",
    mascotProgress: (left: number) => left === 1 ? "Just 1 field left!" : `${left} fields left.`,
    limit: "Monthly document limit reached.",
    plans: "See plans",
    failed: "The PDF could not be generated right now. Try again in a moment.",
    note: "Brazilian reference model. Review the text before sending it for signature.",
    preview: "In the document:",
    optional: "optional",
    other: "Other…",
    ai: "Adjust with AI",
    aiAgain: "Adjust again",
    aiPaid: "paid plans",
    aiFreeTextOff: "Turn off free text to adjust with AI.",
    mine: "My template",
    english: "English",
    mineExpiry: (date: string, days: number) => `Only you can see this copy. It stays in My templates until ${date} (${days <= 1 ? "1 day left" : `${days} days left`}) and is then deleted automatically. Documents you already sent for signature are not affected.`,
    mineEnglish: "Translation of a Brazilian model: the contract still follows Brazilian law (venue in Brazil). Review it carefully before sending.",
    changedTitle: "What the AI changed",
    asked: "Request:",
    changedHint: "Changed passages have a purple bar in the margin (it is not printed).",
    changedMost: "The AI changed almost all of the text, so highlighting starts off.",
    highlight: "Highlight changes",
    undo: "Undo last adjustment",
    undone: "Last adjustment undone.",
    remove: "Delete copy",
    removeTitle: "Delete this copy?",
    removeDesc: "The adjusted copy leaves My templates now. The original model stays in the library, and the adjustment used does not go back to this month's limit.",
    removed: "Copy deleted.",
    original: "See the original model",
    history: (n: number) => n === 1 ? "1 adjustment on this copy" : `${n} adjustments on this copy`,
    expired: (days: number) => `This copy no longer exists. Copies adjusted with AI stay in My templates for ${days} days and are then deleted.`,
    notFound: "Model not found.",
    adjusted: "Done! Adjusted copy saved in My templates.",
  },
};

const INLINE_FORMAT: Record<string, (s: string) => string> = { cpf: formatCpfCnpj, cnpj: formatCpfCnpj, cep: formatCep, phone: formatPhone };

interface EditorDoc { key: string; title: string; html: string; fields: ContractField[]; lang: string; category: string | null }

/** /modelos/:slug (library model) and /modelos/meus/:id (the person's adjusted copy); a new page per address. */
export default function LibraryModelPage() {
  const [location] = useLocation();
  return <LibraryModel key={location} />;
}

function LibraryModel() {
  const { language } = useLanguage();
  const t = language === "en" ? TEXTS.en : TEXTS.pt;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [isMine, mineParams] = useRoute("/modelos/meus/:id");
  const [, modelParams] = useRoute("/modelos/:slug");
  const mineId = isMine ? mineParams?.id || "" : "";
  const slug = isMine ? "" : modelParams?.slug || "";
  const search = useSearch();
  const ajuste = useMemo(() => new URLSearchParams(search).get("ajuste"), [search]);

  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored || !localStorage.getItem("token")) { setLocation("/login"); return; }
    setUser(JSON.parse(stored));
  }, [setLocation]);
  const handleLogout = () => { localStorage.removeItem("token"); localStorage.removeItem("user"); setLocation("/"); };

  const model = useQuery({ queryKey: ["library-model", slug], queryFn: () => fetchModel(slug), enabled: !!slug });
  const mine = useQuery({ queryKey: ["library-mine", mineId], queryFn: () => fetchMyTemplate(mineId), enabled: !!mineId, retry: false });
  const categories = useQuery({ queryKey: ["library-categories"], queryFn: fetchCategories, staleTime: 10 * 60 * 1000 });
  const ai = useAiStatus();
  const aiState = aiButtonState(ai.data);

  const doc: EditorDoc | null = useMemo(() => {
    if (mineId) return mine.data ? { key: mine.data.id, title: mine.data.title, html: mine.data.html, fields: mine.data.fields, lang: mine.data.lang, category: null } : null;
    return model.data ? { key: model.data.slug, title: model.data.title, html: model.data.html, fields: model.data.fields, lang: "pt", category: model.data.category } : null;
  }, [mineId, mine.data, model.data]);
  const loading = mineId ? mine.isLoading : model.isLoading;
  const failed = mineId ? mine.isError : model.isError;
  const category = categories.data?.categories.find((c) => c.key === doc?.category);
  const lang = doc?.lang === "en" ? "en" : "pt";

  const storageKey = mineId ? `fs:mymodel:${mineId}` : `fs:model:${slug}`;
  const [values, setValues] = useState<Record<string, string>>({});
  const [name, setName] = useState("");
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [freeText, setFreeText] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [confirmBlank, setConfirmBlank] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mineBusy, setMineBusy] = useState(false);
  // null = the default: on, unless the AI rewrote most of the text (a translation), where the bars would mark everything
  const [highlight, setHighlight] = useState<boolean | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrefill, setAiPrefill] = useState<string | null>(null);
  const freeRef = useRef<HTMLDivElement>(null);
  const sideRefs = useRef(new Map<string, HTMLElement>());
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const nameRef = useRef(name);
  nameRef.current = name;

  // values survive a trip to the library and back (and are carried into the adjusted copy)
  const loaded = !!doc;
  useEffect(() => {
    if (!doc) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      if (saved?.values) setValues(saved.values);
      setName(saved?.name || doc.title);
    } catch { setName(doc.title); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, storageKey]);
  useEffect(() => {
    if (!loaded) return;
    try { sessionStorage.setItem(storageKey, JSON.stringify({ values, name })); } catch { /* storage may be unavailable */ }
  }, [values, name, loaded, storageKey]);

  // the chat's "Usar e ajustar com IA" arrives with ?ajuste=<what the person asked>
  const lastAjuste = useRef<string | null>(null);
  useEffect(() => {
    if (!ajuste || !loaded || ajuste === lastAjuste.current) return;
    lastAjuste.current = ajuste;
    setAiPrefill(ajuste);
    setAiOpen(true);
  }, [ajuste, loaded]);

  const fields = doc?.fields || [];
  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, ContractField[]>();
    for (const f of fields) {
      const g = f.group || "";
      if (!map.has(g)) { map.set(g, []); order.push(g); }
      map.get(g)!.push(f);
    }
    // the party blocks first, the loose clause fields last
    const sorted = order.filter((g) => g).concat(order.includes("") ? [""] : []);
    return sorted.map((g) => ({ name: g || t.contractGroup, key: g, fields: map.get(g)! }));
  }, [fields, t.contractGroup]);

  const filled = fields.filter((f) => (values[f.key] || "").trim()).length;
  const left = fields.length - filled;
  const hasMarks = !!doc && doc.html.includes(AI_CHANGED);
  const mostChanged = useMemo(() => {
    const blocks = doc?.html.match(/<(?:h1|h2|h3|p|table)\b[^>]*>/g) || [];
    return blocks.length > 0 && blocks.filter((b) => b.includes(AI_CHANGED)).length / blocks.length > 0.6;
  }, [doc]);
  const highlightOn = highlight ?? !mostChanged;

  const setValue = useCallback((key: string, value: string) => setValues((v) => ({ ...v, [key]: value })), []);
  const focusSide = useCallback((key: string) => {
    setActiveKey(key);
    const el = sideRefs.current.get(key);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      window.setTimeout(() => el.focus({ preventScroll: true }), 250);
    }
  }, []);
  const focusChip = useCallback((key: string) => {
    setActiveKey(key);
    const chip = document.querySelector<HTMLElement>(`[data-field="${key}"]`);
    chip?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, []);

  const finish = async () => {
    if (!doc) return;
    setBusy(true);
    try {
      const body = freeText && freeRef.current
        ? { html: freeRef.current.innerHTML, name }
        : { values, name };
      const rendered = mineId ? await renderMine(mineId, body) : await renderModel(slug, body);
      sessionStorage.setItem(PENDING_UPLOAD_KEY, JSON.stringify({ ...rendered, modelSlug: mineId ? mine.data?.baseSlug : slug, modelTitle: doc.title }));
      setLocation("/documents/new?fromModel=1");
    } catch (err: any) {
      const code = err?.body?.errorCode;
      if (code === "DOCUMENT_LIMIT_REACHED") {
        toast({ title: t.limit, description: language === "en" ? err.body?.messageEn : err.body?.messagePt, variant: "destructive", action: <ToastAction altText={t.plans} onClick={() => setLocation("/settings")}>{t.plans}</ToastAction> });
      } else if (mineId && err?.status === 404) {
        toast({ title: t.expired(ai.data?.retentionDays || 30), variant: "destructive" });
      } else {
        toast({ title: t.failed, description: err?.message, variant: "destructive" });
      }
    } finally {
      setBusy(false);
    }
  };

  const onContinue = () => {
    if (!freeText && left > 0) { setConfirmBlank(true); return; }
    void finish();
  };

  /** The adjusted copy: carry the typed values over (same keys) and open it; a second adjustment stays on this page. */
  const onAdjusted = (r: AdjustResult) => {
    const keys = new Set(r.template.fields.map((f) => f.key));
    const kept = Object.fromEntries(Object.entries(valuesRef.current).filter(([k]) => keys.has(k)));
    const currentTitle = doc?.title || "";
    const nextName = !nameRef.current.trim() || nameRef.current === currentTitle ? r.template.title : nameRef.current;
    try { sessionStorage.setItem(`fs:mymodel:${r.template.id}`, JSON.stringify({ values: kept, name: nextName })); } catch { /* storage may be unavailable */ }
    toast({ title: t.adjusted, description: r.summary });
    if (mineId === r.template.id) {
      setValues(kept);
      setName(nextName);
      setHighlight(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setLocation(`/modelos/meus/${r.template.id}`);
    }
  };

  const onUndo = async () => {
    if (!mineId) return;
    setMineBusy(true);
    try {
      const r = await undoMine(mineId);
      queryClient.setQueryData(["library-mine", mineId], r);
      void queryClient.invalidateQueries({ queryKey: ["library-mine-list"] });
      if (nameRef.current === doc?.title) setName(r.title);
      toast({ title: t.undone });
    } catch (err: any) {
      toast({ title: t.failed, description: err?.message, variant: "destructive" });
    } finally {
      setMineBusy(false);
    }
  };

  const onRemove = async () => {
    if (!mineId) return;
    setMineBusy(true);
    try {
      await deleteMine(mineId);
      try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
      void queryClient.invalidateQueries({ queryKey: ["library-mine-list"] });
      queryClient.removeQueries({ queryKey: ["library-mine", mineId] });
      toast({ title: t.removed });
      setLocation("/modelos");
    } catch (err: any) {
      toast({ title: t.failed, description: err?.message, variant: "destructive" });
      setMineBusy(false);
    }
  };

  const openAi = () => { setAiPrefill(null); setAiOpen(true); };

  const mascotText = !doc ? "" : freeText ? t.freeTextHint : left === 0 && fields.length > 0 ? t.mascotDone : filled === 0 ? t.mascotHello(fields.length) : t.mascotProgress(left);
  const mascotMood = left === 0 && fields.length > 0 && !freeText ? "happy" : filled === 0 ? "wave" : "writing";
  const last = mine.data?.history?.[mine.data.history.length - 1];
  const expiresOn = mine.data ? new Intl.DateTimeFormat(language === "en" ? "en-US" : "pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(mine.data.expiresAt)) : "";

  return (
    <AppLayout user={user} onLogout={handleLogout} currentPage="/modelos">
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="model-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link href="/modelos" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="model-back">
            <ArrowLeft className="h-4 w-4" /> {t.back}
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            {mineId && doc && <Badge className="gap-1 bg-violet-600 text-white hover:bg-violet-600" data-testid="mine-badge"><Sparkles className="h-3 w-3" /> {t.mine}</Badge>}
            {mineId && lang === "en" && <Badge variant="outline" data-testid="mine-lang">EN · {t.english}</Badge>}
            {!mineId && doc && category && <Badge variant="outline">{categoryName(category, language)}</Badge>}
          </div>
        </div>

        {loading && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> …</div>}
        {failed && (
          <div className="space-y-2 rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm" data-testid="model-error">
            <p>{mineId ? t.expired(ai.data?.retentionDays || 30) : t.notFound}</p>
            <Link href="/modelos" className="font-medium text-primary underline-offset-4 hover:underline">{t.back}</Link>
          </div>
        )}

        {doc && (
          <>
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div className="min-w-0 flex-1 space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight" data-testid="model-title">{doc.title}</h1>
                <label className="block text-xs text-muted-foreground" htmlFor="model-name">{t.name}</label>
                <Input id="model-name" value={name} onChange={(e) => setName(e.target.value.slice(0, 150))} className="max-w-lg" data-testid="model-name" />
              </div>
              <div className="flex items-center gap-3 rounded-2xl border bg-card px-3 py-2">
                <Mascot mood={mascotMood} size={44} />
                <span className="max-w-xs text-sm" data-testid="model-mascot-text">{mascotText}</span>
              </div>
            </div>

            {mineId && mine.data && (
              <div className="space-y-2" data-testid="mine-banners">
                <div className="flex gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100" data-testid="mine-expiry">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{t.mineExpiry(expiresOn, mine.data.daysLeft)}</span>
                </div>
                {lang === "en" && (
                  <div className="flex gap-2 rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100" data-testid="mine-english">
                    <Flag className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{t.mineEnglish}</span>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
              <div className="min-w-0 space-y-3">
                {mineId && last && (
                  <div className="space-y-2 rounded-2xl border border-violet-200 bg-violet-50/60 p-4 dark:border-violet-900 dark:bg-violet-950/20" data-testid="mine-changes">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h2 className="flex items-center gap-2 text-sm font-semibold"><Wand2 className="h-4 w-4 text-violet-600" /> {t.changedTitle}</h2>
                      {hasMarks && (
                        <label className="flex items-center gap-2 text-xs" data-testid="mine-highlight">
                          {t.highlight}
                          <Switch checked={highlightOn} onCheckedChange={setHighlight} />
                        </label>
                      )}
                    </div>
                    <p className="text-sm" data-testid="mine-summary">{last.summary}</p>
                    <p className="text-xs text-muted-foreground"><span className="font-medium">{t.asked}</span> “{last.instruction}”</p>
                    {hasMarks && highlightOn && <p className="text-xs text-muted-foreground">{t.changedHint}</p>}
                    {hasMarks && !highlightOn && mostChanged && highlight === null && <p className="text-xs text-muted-foreground" data-testid="mine-most-changed">{t.changedMost}</p>}
                    {mine.data!.history.length > 1 && (
                      <details className="text-xs text-muted-foreground" data-testid="mine-history">
                        <summary className="flex cursor-pointer items-center gap-1"><History className="h-3.5 w-3.5" /> {t.history(mine.data!.history.length)}</summary>
                        <ol className="mt-2 list-decimal space-y-1 pl-5">
                          {mine.data!.history.map((h, i) => <li key={i}>“{h.instruction}” — {h.summary}</li>)}
                        </ol>
                      </details>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      {mine.data!.canUndo && (
                        <Button variant="outline" size="sm" className="gap-1" onClick={() => void onUndo()} disabled={mineBusy} data-testid="mine-undo"><Undo2 className="h-3.5 w-3.5" /> {t.undo}</Button>
                      )}
                      <Button variant="outline" size="sm" className="gap-1 text-destructive hover:text-destructive" onClick={() => setConfirmRemove(true)} disabled={mineBusy} data-testid="mine-delete"><Trash2 className="h-3.5 w-3.5" /> {t.remove}</Button>
                      <Link href={`/modelos/${mine.data!.baseSlug}`} className="inline-flex h-9 items-center px-2 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="mine-original">{t.original}</Link>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="min-w-0 flex-1 text-muted-foreground">{freeText ? t.freeTextHint : t.tip}</span>
                  <div className="flex flex-wrap items-center gap-3">
                    {aiState !== "hidden" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className={`gap-1.5 ${aiState === "ready" ? "border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/40" : "text-muted-foreground"}`}
                        onClick={openAi}
                        disabled={freeText}
                        title={freeText ? t.aiFreeTextOff : undefined}
                        data-testid="model-ai"
                        data-state={aiState}
                      >
                        {aiState === "locked" ? <Lock className="h-3.5 w-3.5" /> : <Wand2 className="h-3.5 w-3.5" />}
                        {mineId ? t.aiAgain : t.ai}
                        {aiState === "locked" && <span className="rounded-full bg-muted px-1.5 text-[10px] font-normal">{t.aiPaid}</span>}
                      </Button>
                    )}
                    <label className="flex items-center gap-2 text-sm font-medium" data-testid="model-free-text-toggle">
                      <PenLine className="h-4 w-4" /> {t.freeText}
                      <Switch checked={freeText} onCheckedChange={(on) => { if (on) setFreeText(true); else setConfirmOff(true); }} data-testid="model-free-text-switch" />
                    </label>
                  </div>
                </div>
                {freeText
                  ? <FreeTextView ref={freeRef} html={doc.html} fields={fields} values={values} lang={lang} />
                  : <TemplateView html={doc.html} fields={fields} values={values} activeKey={activeKey} onChange={setValue} onFocusField={focusSide} lang={lang} highlight={highlightOn} />}
                <p className="text-xs text-muted-foreground">{t.note}</p>
              </div>

              <aside className="space-y-3 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto" data-testid="model-side">
                <div className="rounded-2xl border bg-card p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="truncate text-sm font-medium">{t.fields}</h2>
                    {fields.length > 0 && (
                      <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => setValues({})} data-testid="model-clear"><Eraser className="h-3.5 w-3.5" /> {t.clear}</Button>
                    )}
                  </div>
                  {fields.length > 0 ? (
                    <>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span data-testid="model-progress">{left === 0 ? t.allDone : t.progress(filled, fields.length)}</span>
                          {left === 0 && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${fields.length ? (filled / fields.length) * 100 : 0}%` }} /></div>
                      </div>
                      {!freeText && groups.map((g) => (
                        <div key={g.key} className="space-y-2" data-testid={`model-group-${g.key || "contract"}`}>
                          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.name}</h3>
                          {g.fields.map((f) => (
                            <FieldInput key={f.key} field={f} value={values[f.key] || ""} active={activeKey === f.key} onChange={setValue} onFocus={() => focusChip(f.key)} t={t} lang={lang}
                              register={(el) => { if (el) sideRefs.current.set(f.key, el); else sideRefs.current.delete(f.key); }} />
                          ))}
                        </div>
                      ))}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t.noFields}</p>
                  )}
                </div>
                <Button className="w-full gap-2" size="lg" onClick={onContinue} disabled={busy} data-testid="model-continue">
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {t.generating}</> : <><Sparkles className="h-4 w-4" /> {t.continueBtn} <ArrowRight className="h-4 w-4" /></>}
                </Button>
              </aside>
            </div>

            <AiAdjustDialog
              open={aiOpen}
              onOpenChange={setAiOpen}
              target={mineId ? { kind: "mine", id: mineId } : { kind: "model", slug }}
              docLang={lang}
              docChars={doc.html.length}
              initialInstruction={aiPrefill}
              onDone={onAdjusted}
            />
          </>
        )}
      </div>

      <AlertDialog open={confirmOff} onOpenChange={setConfirmOff}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.freeTextOffTitle}</AlertDialogTitle>
            <AlertDialogDescription>{t.freeTextOffDesc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.keep}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setFreeText(false); setConfirmOff(false); }} data-testid="model-free-text-off">{t.discard}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmBlank} onOpenChange={setConfirmBlank}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.blankTitle}</AlertDialogTitle>
            <AlertDialogDescription>{t.blankDesc(left)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="model-blank-back">{t.fill}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmBlank(false); void finish(); }} data-testid="model-blank-continue">{t.goOn}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.removeTitle}</AlertDialogTitle>
            <AlertDialogDescription>{t.removeDesc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.keep}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { setConfirmRemove(false); void onRemove(); }} data-testid="mine-delete-confirm">{t.remove}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

interface FieldInputProps {
  field: ContractField;
  value: string;
  active: boolean;
  onChange: (key: string, value: string) => void;
  onFocus: () => void;
  register: (el: HTMLElement | null) => void;
  t: typeof TEXTS.pt;
  lang: string;
}

function FieldInput({ field, value, active, onChange, onFocus, register, t, lang }: FieldInputProps) {
  const preview = ["money", "number", "percent", "date", "cpf", "cnpj", "cep", "phone"].includes(field.type) ? renderValue(field, value, lang) : null;
  const listId = field.options || field.suggest ? `opts-${field.key}` : undefined;
  const options = field.options || (field.suggest ? [field.suggest] : []);
  const common = {
    id: `field-${field.key}`,
    value,
    onFocus,
    "data-testid": `field-${field.key}`,
    className: active ? "ring-2 ring-primary/40" : "",
  };
  return (
    <div className="space-y-1">
      <label htmlFor={`field-${field.key}`} className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium">{field.label}</span>
        {field.hint && field.hint.toLowerCase() !== field.label.toLowerCase() && <span className="truncate text-muted-foreground" title={field.hint}>{field.hint}</span>}
      </label>
      {field.type === "long" ? (
        <Textarea {...common} rows={3} onChange={(e) => onChange(field.key, e.target.value)} ref={(el) => register(el)} />
      ) : field.type === "date" ? (
        <Input {...common} type="date" onChange={(e) => onChange(field.key, e.target.value)} ref={(el) => register(el)} />
      ) : (
        <>
          <Input
            {...common}
            list={listId}
            inputMode={["money", "number", "percent"].includes(field.type) ? "decimal" : ["cpf", "cnpj", "cep", "phone"].includes(field.type) ? "numeric" : "text"}
            placeholder={field.type === "money" ? (lang === "en" ? "0.00" : "0,00") : field.type === "percent" ? "%" : field.suggest || ""}
            onChange={(e) => onChange(field.key, e.target.value)}
            onBlur={(e) => { const f = INLINE_FORMAT[field.type]; if (f && e.target.value.trim()) onChange(field.key, f(e.target.value)); }}
            ref={(el) => register(el)}
          />
          {listId && <datalist id={listId}>{options.map((o) => <option key={o} value={o} />)}</datalist>}
        </>
      )}
      {preview && preview !== value && <p className="text-xs text-muted-foreground"><span className="opacity-70">{t.preview}</span> {preview}</p>}
    </div>
  );
}
